import { measurePaintedFrame, toRendererCoordinates } from './previewFrameGeometry';
import { calculateSnapDistance, clampToPositionRange, collectSnapTargets, snapBoxCenter, toBaseUnits } from './canvasElementSnapping';

// Drags elements around the preview canvas instead of nudging their X/Y Position sliders. The element
// is moved directly while the pointer is down and the store is written once on release, so the
// settings panel re-renders once per drag rather than once per frame.
//
// It knows nothing about what it is holding: a picture, a line of text and the visualizer all arrive
// as a CanvasDragTarget and are moved through the same few methods.
export default class CanvasElementDragController {

    constructor(canvas, { getTargets, getMaxPosition, highlight, snapGuides }) {
        this.canvas = canvas;
        this.getTargets = getTargets;
        this.getMaxPosition = getMaxPosition;
        this.highlight = highlight;
        this.snapGuides = snapGuides;

        this.dragState = null;
        // Where the pointer was last seen over the canvas, in base units, for refreshHover.
        this.lastPointer = null;

        this.canvas.addEventListener('pointerdown', this.handlePointerDown);
        this.canvas.addEventListener('pointermove', this.handlePointerMove);
        this.canvas.addEventListener('pointerup', this.handlePointerUp);
        this.canvas.addEventListener('pointercancel', this.handlePointerUp);
        this.canvas.addEventListener('pointerleave', this.handlePointerLeave);
    }

    handlePointerDown = (event) => {
        // A second finger down while the first is dragging must not take the element from the first,
        // which would leave it held by a target that is never told to let go.
        if (this.dragState)
            return;

        const targets = this.getTargets();
        const target = this.findTargetAt(targets, this.toBaseUnits(event));

        if (!target)
            return;

        target.startDrag();

        const positionAtGrab = target.getPosition();

        this.dragState = {
            pointerId: event.pointerId,
            target: target,
            positionAtGrab: positionAtGrab,
            currentPosition: positionAtGrab,
            grabbedAt: this.toBaseUnits(event),
            pendingPosition: null,
            scheduledFrame: null,
            // The alignment lines and the size of the element being dragged are both fixed for the
            // length of the drag, so they are measured once here instead of on every frame.
            snapTargets: collectSnapTargets(target, targets),
            snapBox: this.measureSnapBox(target, positionAtGrab),
            offsetFromPosition: target.measureOffsetFromPosition(positionAtGrab),
            snapDistance: calculateSnapDistance(measurePaintedFrame(this.canvas).scale)
        };

        // Capture keeps the drag alive when the pointer leaves the canvas; it is not essential, and a
        // pointer the browser no longer knows about makes it throw.
        try { this.canvas.setPointerCapture(event.pointerId); } catch { /* drag continues without capture */ }

        this.setCursor('grabbing');
        this.highlight.showAround(target);
    };

    // The element's own box, and how far it sits from the position that is stored for it. A picture and
    // the visualizer are drawn around their position, but a line of text hangs off it to one side or
    // the other depending on its anchor point, and aligning the position rather than the box would put
    // a left-aligned line on the centre line by its left edge.
    measureSnapBox(target, position) {
        const box = target.measureBox();

        if (!box)
            return null;

        return {
            halfWidth: box.halfWidth,
            halfHeight: box.halfHeight,
            offsetX: box.centerX - position.x,
            offsetY: box.centerY - position.y
        };
    }

    handlePointerMove = (event) => {
        if (!this.dragState) {
            this.lastPointer = this.toBaseUnits(event);
            this.showHoveredTarget(this.findTargetAt(this.getTargets(), this.lastPointer));
            return;
        }

        if (event.pointerId !== this.dragState.pointerId)
            return;

        const pointerInBaseUnits = this.toBaseUnits(event);
        const { grabbedAt, positionAtGrab } = this.dragState;

        // Move by the delta since the grab, not to the pointer itself, so the element does not jump its
        // middle under the cursor when it was grabbed off-centre. Snapping is left to the frame below:
        // it is the more expensive half, and doing it here would repeat it for every one of the several
        // pointer reports that share a frame.
        this.dragState.pendingPosition = {
            x: positionAtGrab.x + (pointerInBaseUnits.x - grabbedAt.x),
            y: positionAtGrab.y + (pointerInBaseUnits.y - grabbedAt.y)
        };

        this.scheduleMove();
    };

    // A pointer reports far more often than the display can show, so the element is moved once per
    // animation frame. The drag still tracks the pointer visually and the work drops to one move.
    scheduleMove() {
        if (this.dragState.scheduledFrame !== null)
            return;

        this.dragState.scheduledFrame = window.requestAnimationFrame(() => {
            if (!this.dragState)
                return;

            this.dragState.scheduledFrame = null;
            this.movePendingPosition();
        });
    }

    movePendingPosition() {
        const { pendingPosition, currentPosition, target } = this.dragState;

        if (!pendingPosition)
            return;

        this.dragState.pendingPosition = null;

        const snapped = this.snapToAlignmentLines(pendingPosition);
        const position = this.clampToSliderRange(snapped.position);

        // Ahead of the no-op check below on purpose: an element nudged about while a line is holding it
        // does not move at all, and that is exactly the moment the line is worth showing.
        this.snapGuides.show(this.guidesSurvivingTheClamp(snapped, position));

        // Positions are whole slider steps, so a sub-step pointer move is a no-op worth skipping.
        if (position.x === currentPosition.x && position.y === currentPosition.y)
            return;

        this.dragState.currentPosition = position;

        target.moveTo(position);
        this.highlight.showAround(target);
    }

    // Alignment is decided on the element's box and handed back as a position, which is what gets
    // stored: the two differ by however far the box hangs off the position, measured at the grab.
    snapToAlignmentLines(position) {
        const { snapTargets, snapBox, snapDistance } = this.dragState;

        // An element whose texture had not arrived when the drag started has no size to align by.
        if (!snapBox)
            return { position: position, guides: [] };

        const snapped = snapBoxCenter(
            { x: position.x + snapBox.offsetX, y: position.y + snapBox.offsetY },
            snapBox,
            snapTargets,
            snapDistance
        );

        return {
            position: { x: snapped.center.x - snapBox.offsetX, y: snapped.center.y - snapBox.offsetY },
            guides: snapped.guides
        };
    }

    // A snap that the position limits then pulled back off its line is not a snap any more, and a guide
    // drawn where the element is not would be a lie. Rounding to whole steps is allowed to move it,
    // since half a base unit is well under a screen pixel.
    guidesSurvivingTheClamp(snapped, clampedPosition) {
        return snapped.guides.filter(guide => Math.abs(clampedPosition[guide.axis] - snapped.position[guide.axis]) <= 0.5);
    }

    // The outline follows whatever is under the pointer, so it doubles as the hint that this particular
    // element is the one a drag would pick up.
    showHoveredTarget(target) {
        this.setCursor(target ? 'grab' : '');

        if (target)
            this.highlight.showAround(target);
        else
            this.highlight.hide();
    }

    // Looks again at what is under a pointer that has not moved. The hover is otherwise settled only
    // by pointer events, and while the preview plays a line of lyrics can end, or a new one begin,
    // under a pointer that is holding still: the outline would then stay drawn around nothing, or the
    // new line would take no grab until the pointer moved.
    refreshHover() {
        if (this.dragState || !this.lastPointer)
            return;

        this.showHoveredTarget(this.findTargetAt(this.getTargets(), this.lastPointer));
    }

    handlePointerLeave = (event) => {
        this.lastPointer = null;

        // A captured pointer keeps reporting from outside the canvas, and dropping the outline mid-drag
        // would be the wrong signal.
        if (this.dragState)
            return;

        // Reaching for a corner square leaves the canvas, because the squares sit in the overlay above
        // it. Hiding the outline then would take the square out from under the pointer just as the user
        // arrives at it.
        if (this.highlight.containsElement(event.relatedTarget))
            return;

        this.setCursor('');
        this.highlight.hide();
        this.snapGuides.hide();
    };

    handlePointerUp = (event) => {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId)
            return;

        // The last pointer position may still be waiting on its frame; releasing must not drop it.
        this.cancelScheduledMove();
        this.movePendingPosition();

        const { target, positionAtGrab, currentPosition } = this.dragState;
        const moved = currentPosition.x !== positionAtGrab.x || currentPosition.y !== positionAtGrab.y;

        this.dragState = null;
        target.stopDrag();
        try { this.canvas.releasePointerCapture(event.pointerId); } catch { /* capture was never taken */ }

        this.setCursor('grab');
        this.highlight.showAround(target);
        this.snapGuides.hide();

        // A plain click moved nothing, and storing an unchanged position would still cost the user an
        // undo step that appears to do nothing.
        if (moved)
            target.commit(currentPosition);
    };

    cancelScheduledMove() {
        if (!this.dragState || this.dragState.scheduledFrame === null)
            return;

        window.cancelAnimationFrame(this.dragState.scheduledFrame);
        this.dragState.scheduledFrame = null;
    }

    // The list arrives back to front, so the search runs backwards and the first hit is the element the
    // user can actually see under the pointer: a picture is picked up ahead of the text behind it, and
    // the text ahead of the visualizer behind that.
    findTargetAt(targets, pointer) {
        for (let index = targets.length - 1; index >= 0; index--) {
            if (targets[index].containsPoint(pointer))
                return targets[index];
        }

        return null;
    }

    toBaseUnits(event) {
        return toBaseUnits(toRendererCoordinates(this.canvas, event));
    }

    // The limits are read per frame rather than once per drag, because they belong to the aspect ratio
    // and not to the gesture. The box offset is the element's own, measured once at the grab.
    clampToSliderRange(position) {
        return clampToPositionRange(position, this.getMaxPosition(), this.dragState.offsetFromPosition);
    }

    setCursor(cursor) {
        if (this.canvas.style.cursor !== cursor)
            this.canvas.style.cursor = cursor;
    }

    destroy() {
        this.cancelScheduledMove();
        this.dragState?.target.stopDrag();
        this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
        this.canvas.removeEventListener('pointermove', this.handlePointerMove);
        this.canvas.removeEventListener('pointerup', this.handlePointerUp);
        this.canvas.removeEventListener('pointercancel', this.handlePointerUp);
        this.canvas.removeEventListener('pointerleave', this.handlePointerLeave);
        this.dragState = null;
        this.highlight.hide();
        this.snapGuides.hide();
    }
}
